/**
 * Print on demand — the per-title switch and prices.
 *
 * Lives under the Payments group on the Settings page, beside the
 * membership plans and the renewal cadence, because it is the same kind of
 * thing: an operator setting stored in our own database rather than a
 * credential read from `.env`.
 *
 * The switch decides what a member sees on an issue page — a Buy control, or
 * Coming soon with the Interested question. Turning it off does NOT stop an order that
 * has already been charged; those still have to ship. It stops new sales and
 * cancels anything still inside its publish hold.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { api, ApiError } from '../api/client'
import { Button } from './Button'
import {
  centsToDollars,
  dollarsToCents,
  interestedSummary,
  membershipLabel,
  sentimentLine,
  settingsProblem,
  sourcesLabel,
  type InterestedList,
  type PrintSetting,
} from '../lib/print'

const PUB_NAMES: Record<string, string> = {
  flight_journal: 'Flight Journal',
  model_airplane_news: 'Model Airplane News',
  rc_car_action: 'RC Car Action',
  diecast: 'Die Cast X',
}

/**
 * @param embedded  Rendered inside a Panel of its own (the Print screen)
 *                  rather than appended to the Settings page's Payments
 *                  group, so it drops the divider and the heading that
 *                  would otherwise repeat the Panel's label.
 */
export function PrintSettingsPanel({ embedded = false }: { embedded?: boolean }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['settings', 'print'],
    queryFn: () => api.get<{ items: PrintSetting[] }>('/api/admin/settings/print'),
  })

  return (
    <div className={embedded ? '' : 'mt-4 pt-4 border-t border-ink-500/60'}>
      {!embedded && <p className="label mb-1">Printed copies (print on demand)</p>}
      <p className="label mb-3 text-paper-trace">
        Off shows readers “Coming soon” and asks Interested / Not interested.
        On shows a Buy control at the price below. Turning a title off stops new sales and
        cancels orders still inside their publish hold — it does not stop an
        order already charged, which still has to ship.
      </p>
      {isLoading && <p className="label">Loading…</p>}
      {error instanceof Error && <p className="text-red-400">{error.message}</p>}
      {data && (
        <div className="space-y-3">
          {data.items.map((s) => (
            <PrintTitleRow key={s.publication_slug} setting={s} />
          ))}
        </div>
      )}
    </div>
  )
}

function PrintTitleRow({ setting }: { setting: PrintSetting }) {
  const qc = useQueryClient()
  const [enabled, setEnabled] = useState(setting.enabled)
  const [price, setPrice] = useState(centsToDollars(setting.price_cents))
  const [intlEnabled, setIntlEnabled] = useState(setting.intl_enabled)
  const [intlPrice, setIntlPrice] = useState(centsToDollars(setting.intl_price_cents))
  const [askEmail, setAskEmail] = useState(setting.ask_visitor_email)
  const [showList, setShowList] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)

  const priceCents = dollarsToCents(price)
  const intlPriceCents = dollarsToCents(intlPrice)

  const dirty =
    enabled !== setting.enabled
    || intlEnabled !== setting.intl_enabled
    || priceCents !== setting.price_cents
    || intlPriceCents !== setting.intl_price_cents
    || askEmail !== setting.ask_visitor_email

  const save = useMutation({
    mutationFn: () => api.put('/api/admin/settings/print', {
      publication_slug: setting.publication_slug,
      enabled,
      price_cents: priceCents,
      intl_enabled: intlEnabled,
      intl_price_cents: intlEnabled ? intlPriceCents : null,
      ask_visitor_email: askEmail,
    }),
    onSuccess: () => {
      setRefusal(null)
      void qc.invalidateQueries({ queryKey: ['settings', 'print'] })
    },
    onError: (e) => setRefusal(
      e instanceof ApiError && typeof e.detail === 'string'
        ? e.detail
        : e instanceof Error ? e.message : 'That did not save.',
    ),
  })

  // Checked against what is in the boxes, not what is stored — the whole
  // point is to refuse the save before it becomes a live setting that turns
  // every overseas member away in silence.
  const problem = settingsProblem({
    ...setting,
    enabled,
    price_cents: priceCents ?? 0,
    intl_enabled: intlEnabled,
    intl_price_cents: intlEnabled ? intlPriceCents : null,
  })

  return (
    <div className="border border-ink-500/60 px-3 py-3">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <div className="min-w-[190px]">
          <span className="text-paper text-[13px]">
            {PUB_NAMES[setting.publication_slug] ?? setting.publication_slug}
          </span>
          {setting.interest_count > 0 && (
            <p className="label text-radar-cyan">
              {setting.interest_count} waiting to be told
              {setting.interest_email_only > 0 && (
                <> ({setting.interest_email_only} not members — first launch email for them)</>
              )}
            </p>
          )}
          {sentimentLine(setting) && (
            <p className="label text-paper-dim" data-testid="print-sentiment-line">
              {sentimentLine(setting)}
            </p>
          )}
          {(setting.sentiment_interested > 0 || setting.interest_count > 0) && (
            <button type="button" className="label-amber hover:underline"
                    data-testid="print-interested-toggle"
                    onClick={() => setShowList((v) => !v)}>
              {showList ? 'Hide interested readers' : 'View interested readers →'}
            </button>
          )}
        </div>

        {/* The label names what ticking the box DOES, and never changes.
            A checkbox whose caption flipped to "Coming soon" when unticked
            read as "tick me to make it coming soon" — the opposite of what
            it does. What the member ends up seeing is said separately. */}
        <label className="flex items-center gap-2" data-testid="print-enabled">
          <input type="checkbox" checked={enabled}
                 onChange={(e) => setEnabled(e.target.checked)} />
          <span className="label">On sale</span>
        </label>

        <span className="label text-paper-trace">
          Members see: {enabled ? 'Buy' : 'Coming soon'}
        </span>

        <Money label="Price per issue" value={price} onChange={setPrice} />

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={intlEnabled}
                 onChange={(e) => setIntlEnabled(e.target.checked)} />
          <span className="label">Ship abroad</span>
        </label>

        {intlEnabled && (
          <Money label="Price abroad" value={intlPrice} onChange={setIntlPrice} />
        )}

        {/* Only does anything while print is off: it is the question page
            that asks. Members are never asked; we already know them. */}
        <label className="flex items-center gap-2" data-testid="print-ask-email"
               title="After a signed-out visitor answers Interested, ask for an optional email">
          <input type="checkbox" checked={askEmail}
                 onChange={(e) => setAskEmail(e.target.checked)} />
          <span className="label">Ask visitors for email</span>
        </label>

        <Button size="sm" className="ml-auto" data-testid="print-save"
                disabled={!dirty || problem !== null || save.isPending}
                onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {problem && <p className="label mt-2 text-radar-red">{problem}</p>}
      {refusal && <p className="label mt-2 text-radar-red">{refusal}</p>}
      {showList && <InterestedReaders slug={setting.publication_slug} />}
    </div>
  )
}

/** Who wants print on this title, by name (2026-10-16). Members who said
 *  Interested (or left a notify-me before it was retired), then addresses
 *  visitors typed. Anonymous answers are only a count. */
function InterestedReaders({ slug }: { slug: string }) {
  const params = new URLSearchParams({ publication: slug })
  const { data, isLoading, error } = useQuery({
    queryKey: ['settings', 'print', 'interested', slug],
    queryFn: () => api.get<InterestedList>(`/api/admin/settings/print/interested?${params}`),
  })
  return (
    <div className="mt-3 pt-3 border-t border-ink-500/60" data-testid="print-interested-list">
      {isLoading && <p className="label">Loading…</p>}
      {error instanceof Error && <p className="text-red-400">{error.message}</p>}
      {data && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <p className="label text-paper-dim">{interestedSummary(data)}</p>
            <a href={`/api/admin/settings/print/interested.csv?${params}`}
               className="ml-auto font-mono text-[11px] uppercase tracking-label px-3 py-1.5
                          border border-amber-glow/50 text-amber-glow
                          hover:bg-amber-wash hover:border-amber-glow">
              Download CSV
            </a>
          </div>
          {data.items.length === 0 ? (
            <p className="label text-paper-trace">No one we can name yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="label text-left text-paper-trace">
                    <th className="py-1 pr-3 font-normal">Name</th>
                    <th className="py-1 pr-3 font-normal">Email</th>
                    <th className="py-1 pr-3 font-normal">Account</th>
                    <th className="py-1 pr-3 font-normal">Membership</th>
                    <th className="py-1 pr-3 font-normal">How</th>
                    <th className="py-1 font-normal">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((p) => (
                    <tr key={`${p.kind}-${p.subscriber_id ?? p.email}`}
                        className="border-t border-ink-500/40 text-paper">
                      <td className="py-1 pr-3">
                        {p.kind === 'member' && p.subscriber_id !== null ? (
                          <Link to={`/subscribers/${p.subscriber_id}`}
                                className="hover:text-amber-glow hover:underline">
                            {p.name ?? '(no name)'}
                          </Link>
                        ) : <span className="text-paper-trace">Visitor</span>}
                      </td>
                      <td className="py-1 pr-3 font-mono">{p.email ?? '—'}</td>
                      <td className="py-1 pr-3 font-mono">{p.account ?? '—'}</td>
                      <td className="py-1 pr-3">{p.kind === 'member' ? membershipLabel(p) : '—'}</td>
                      <td className="py-1 pr-3">{sourcesLabel(p.sources)}</td>
                      <td className="py-1 font-mono">{p.since.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Money({ label, value, onChange }: {
  label: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <label className="grid gap-1">
      <span className="label">{label}</span>
      <div className="flex items-center gap-1">
        <span className="font-mono text-[12px] text-paper-dim">$</span>
        <input value={value} onChange={(e) => onChange(e.target.value)}
               inputMode="decimal" placeholder="19.99"
               className="bg-ink-900 border border-ink-500 px-2 py-1.5 font-mono
                          text-[12px] text-paper w-24" />
      </div>
    </label>
  )
}
